import { useMemo } from 'react';
import { useAgentsMap, useListFetch } from '../../hooks';
import { getAgentBillingById, getConversationBilling } from '../../api';
import { getAgentTitle, processStringValue } from '../../utilities';
import { useBillingLastRefreshedDate } from '../billing/hooks/useBillingLastRefreshedDate';

export const useConversationsBilling = (
  agentId: string,
  conversationId: string | undefined
) => {
  const agentsShortMap = useAgentsMap();
  const { lastRefreshedDate, isLastRefreshedDateLoading } =
    useBillingLastRefreshedDate();

  const {
    nodes: rawNodes,
    filters,
    setFilters,
    setSort,
    intializing,
    ...rest
  } = useListFetch<Record<string, any>>(
    async params => {
      const response = await getConversationBilling({
        agentId,
        conversationId,
        startTime: processStringValue(params?.filters?.started_at),
        endTime: processStringValue(params?.filters?.ended_at),
        page: params?.pagination?.page ?? 0,
        size: params?.pagination?.perPage ?? 1,
        sort: params?.sort?.name
          ? [`${params.sort.name},${params.sort.direction}`]
          : [],
      });

      const agentResponse = await getAgentBillingById({
        agentId,
        startTime: processStringValue(params?.filters?.started_at),
        endTime: processStringValue(params?.filters?.ended_at),
      });

      return {
        total: response?.totalElements ?? 0,
        content: response?.content ?? [],
        totalText: agentResponse?.textMinutes,
        totalVoice: agentResponse?.voiceMinutes,
      };
    },
    {
      withQueryParams: true,
      initialSort: { name: 'startTime', direction: 'desc' },
    }
  );

  const enrichedData = useMemo(() => {
    return {
      ...rest.rawData,
      agentTitle: getAgentTitle(agentsShortMap, agentId),
      totalVideo: 0,
    };
  }, [rest.rawData, agentsShortMap, agentId]);

  return {
    setSort,
    filters,
    setFilters,
    intializing,
    conversationsBilling: rawNodes ?? [],
    lastRefreshedDate,
    isLastRefreshedDateLoading,
    ...rest,
    rawData: enrichedData,
  };
};
